/* eslint-disable no-console */
import axios from 'axios';
import { apiClient, apiEndpoint } from './api.services';
import { API } from '../constants/api.constants';
import { localStorageService } from './localStorage.service';

/**
 * Fetch product details for purchase order creation
 * @param {string} devOrderId - Development Order ID
 * @returns {Promise} API response with product details
 */
export const fetchProductDetailsForPO = async (devOrderId) => {
  try {
    return await apiClient.get(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.GET_PRODUCT_DETAILS}/${devOrderId}`);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const fetchCompanyDetailsForPO = async () => {
  try {
    return await apiClient.get(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.GET_COMPANY_DETAILS}`);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Create a new product purchase order
 * @param {Object} orderData - Purchase order data
 * @returns {Promise} API response
 */
export const createPurchaseOrder = async (orderData) => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const payload = {
      ...orderData,
      timezone,
    };
    return await apiClient.post(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.GENERAL_PRODUCT_PO}`, payload);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Update an existing product purchase order
 * @param {string} orderId - Purchase order ID
 * @param {Object} orderData - Updated purchase order data
 * @returns {Promise} API response
 */
export const updatePurchaseOrder = async (orderId, orderData) => {
  try {
    return await apiClient.put(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.GENERAL_PRODUCT_PO}/${orderId}`, orderData);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Update actual fields of a product purchase order
 * @param {string} orderId - Purchase order ID
 * @param {Object} updates - Actual field updates (e.g., actualTotalUnits, actualApproveByDate, etc.)
 * @returns {Promise} API response
 */
export const updateActualFields = async (orderId, updates) => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const payload = {
      ...updates,
      timezone,
    };
    return await apiClient.put(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.GENERAL_PRODUCT_PO}/${orderId}/actual-fields`, payload);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Delete a product purchase order
 * @param {string} orderId - Purchase order ID
 * @returns {Promise} API response
 */
export const deletePurchaseOrder = async (orderId) => {
  try {
    return await apiClient.delete(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.GENERAL_PRODUCT_PO}/${orderId}`);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Fetch new purchase orders only
 * @param {Object} params - Query parameters
 * @param {number} [params.pageSize=20] - Number of items per page
 * @param {number} [params.pageIndex=1] - Page number (1-based)
 * @param {string} [params.searchText] - Search text for filtering
 * @param {Object} [params.filters] - Additional filters including devOrderId
 * @returns {Promise} API response
 */
export const fetchNewPurchaseOrders = async (params = {}) => {
  try {
    // Use the new PO API endpoint for new purchase orders
    const requestData = {
      pageIndex: params.pageIndex || 1,
      pageSize: params.pageSize || 20,
      searchText: params.searchText || '',
      filters: {
        devOrderId: params.filters?.devOrderId,
        supplierIds: params.filters?.supplierIds || [],
        colourwayIds: params.filters?.colourwayIds || [],
        statuses: params.filters?.statuses || [],
      },
    };

    return await apiClient.post(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.SEARCH_PRODUCT_PO}`, requestData);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Fetch old purchase orders only
 * @param {Object} params - Query parameters
 * @param {number} [params.pageSize=20] - Number of items per page
 * @param {number} [params.pageIndex=1] - Page number (1-based)
 * @param {string} [params.searchText] - Search text for filtering
 * @param {Object} [params.filters] - Additional filters including devOrderId
 * @returns {Promise} API response
 */
export const fetchOldPurchaseOrders = async (params = {}) => {
  try {
    // Use the old PO API endpoint for old purchase orders
    const queryParams = {
      pageIndex: params.pageIndex || 1,
      pageSize: params.pageSize || 20,
      devOrderId: params.filters?.devOrderId,
      searchText: params.searchText,
      supplier: params.filters?.supplierIds,
      color: params.filters?.colourwayIds,
      status: params.filters?.statuses,
      // Add flag to indicate we only want old POs
      onlyOldPOs: true,
    };

    // Remove undefined values
    Object.keys(queryParams).forEach((key) => {
      if (queryParams[key] === undefined || queryParams[key] === null || (Array.isArray(queryParams[key]) && queryParams[key].length === 0)) {
        delete queryParams[key];
      }
    });

    return await apiClient.get(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.PURCHASE_ORDERS_LIST}`, {
      params: queryParams,
    });
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Fetch purchase orders with filters and pagination (legacy method)
 * @param {Object} params - Query parameters
 * @returns {Promise} API response
 */
export const fetchPurchaseOrders = async (params = {}) => {
  // For backward compatibility, delegate to fetchNewPurchaseOrders
  return fetchNewPurchaseOrders(params);
};

/**
 * Bulk delete purchase orders
 * @param {Array<string>} orderIds - Array of purchase order IDs
 * @returns {Promise} API response
 */
export const bulkDeletePurchaseOrders = async (orderIds) => {
  try {
    const ids = Array.isArray(orderIds) ? orderIds.join(',') : orderIds;
    return await apiClient.delete(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.BULK_DELETE}?orderIds=${ids}`);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Set default purchase order
 * @param {string} orderId - Purchase order ID
 * @returns {Promise} API response
 */
export const setDefaultPurchaseOrder = async (orderId) => {
  try {
    return await apiClient.put(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.SET_DEFAULT}/${orderId}`);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Get purchase order templates
 * @param {Object} params - Query parameters
 * @returns {Promise} API response
 */
export const getPurchaseOrderTemplates = async (params = {}) => {
  try {
    return await apiClient.get(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.TEMPLATES}`, {
      params,
    });
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Save (create/update) purchase order template
 * @param {Object} templateData - Template data
 * @returns {Promise} API response
 */
export const savePurchaseOrderTemplate = async (templateData) => {
  try {
    if (templateData._id) {
      const { _id, ...updateData } = templateData;
      return await apiClient.put(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.TEMPLATES}/${_id}`, updateData);
    }
    return await apiClient.post(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.TEMPLATES}`, templateData);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Delete purchase order template
 * @param {string} templateId - Template ID
 * @returns {Promise} API response
 */
export const deletePurchaseOrderTemplate = async (templateId) => {
  try {
    return await apiClient.delete(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.TEMPLATES}/${templateId}`);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Export purchase order data
 * @param {Object} params - Export parameters
 * @returns {Promise} API response
 */
export const exportPurchaseOrderData = async (params) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.EXPORT}`, params);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Duplicate purchase order
 * @param {string} orderId - Original order ID
 * @param {Object} orderData - New order data
 * @returns {Promise} API response
 */
export const duplicatePurchaseOrder = async (orderId, orderData) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.DUPLICATE}/${orderId}`, orderData);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Get purchase order history
 * @param {string} orderId - Purchase order ID
 * @returns {Promise} API response
 */
export const getPurchaseOrderHistory = async (orderId) => {
  try {
    return await apiClient.get(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.HISTORY}/${orderId}`);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Fetch purchase order details by ID for editing
 * @param {string} orderId - Purchase order ID
 * @returns {Promise} API response with purchase order details
 */
export const fetchPurchaseOrderDetails = async (orderId) => {
  try {
    return await apiClient.get(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.GENERAL_PRODUCT_PO}/${orderId}`);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Fetch filtered development orders by supplier and optionally by factory and brand
 * @param {string} supplierId - Supplier ID
 * @param {string} [factoryId] - Factory ID (optional)
 * @param {string} [brandId] - Brand ID (optional)
 * @returns {Promise} API response with filtered development orders
 */
export const fetchFilteredDevelopmentOrders = async (supplierId, factoryId = null, brandId = null) => {
  try {
    let url = `${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.FILTER_DEVELOPMENT_ORDERS}?supplierId=${supplierId}`;

    if (factoryId) {
      url += `&factoryId=${factoryId}`;
    }
    if (brandId) {
      url += `&brandId=${brandId}`;
    }

    return await apiClient.get(url);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Fetch factories for a specific supplier
 * @param {string} supplierId - Supplier ID
 * @param {Object} params - Query parameters
 * @param {number} [params.pageSize=100] - Number of items per page
 * @param {number} [params.pageIndex=1] - Page number (1-based)
 * @param {string} [params.searchText] - Search text for filtering
 * @returns {Promise} API response with factory list
 */
export const fetchSupplierFactories = async (supplierId, params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      pageSize: params.pageSize || 100,
      pageIndex: params.pageIndex || 1,
      ...(params.searchText && { searchText: params.searchText }),
    }).toString();

    return await apiClient.get(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.GET_SUPPLIER_FACTORIES}/${supplierId}?${queryParams}`);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Create a new purchase order file record
 * @param {Object} fileData - File metadata to save
 * @param {string} fileData.devOrderId - Development Order ID
 * @param {string} fileData.poId - Purchase Order ID
 * @param {string} fileData.s3key - S3 object key (file path)
 * @param {string} fileData.documentName - Document name
 * @param {string} fileData.note - Notes
 * @param {string} fileData.status - Status
 * @param {boolean} fileData.visibleToSupplier - Visibility to supplier
 * @param {string} fileData.fileName - Original file name
 * @param {string} fileData.fileSize - File size as string
 * @param {string} fileData.mimeType - MIME type
 * @param {string} fileData.documentId - Document ID
 * @returns {Promise} API response
 */
export const createOrderFile = async (fileData) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.CREATE_ORDER_FILE}`, fileData);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Get purchase order files list
 * @param {string} poId - Purchase Order ID
 * @param {Object} params - Query parameters
 * @param {number} [params.pageIndex=1] - Page number
 * @param {number} [params.pageSize=20] - Items per page
 * @returns {Promise} API response with files list
 */
export const getOrderFilesList = async (poId, params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      pageIndex: params.pageIndex || 1,
      pageSize: params.pageSize || 20,
    }).toString();

    return await apiClient.get(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.GET_ORDER_FILES_LIST}/${poId}/files?${queryParams}`);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getOrderFileDetails = async (fileId) => {
  try {
    return await apiClient.get(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.GET_ORDER_FILE_DETAILS}/${fileId}`);
  } catch (error) {
    console.error('Error in getOrderFileDetails:', error);
    throw error;
  }
};

export const updateOrderFile = async (fileId, fileData) => {
  try {
    return await apiClient.put(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.UPDATE_ORDER_FILE}/${fileId}`, fileData);
  } catch (error) {
    console.error('Error in updateOrderFile:', error);
    throw error;
  }
};

/**
 * Delete a single purchase order file
 * @param {string} fileId - File ID to delete
 * @returns {Promise} API response
 */
export const deleteOrderFile = async (fileId) => {
  try {
    return await apiClient.delete(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.DELETE_ORDER_FILE}/${fileId}`);
  } catch (error) {
    console.error('Error in deleteOrderFile:', error);
    throw error;
  }
};

/**
 * Bulk delete purchase order files
 * @param {Array<string>} fileIds - Array of file IDs to delete
 * @returns {Promise} API response
 */
export const bulkDeleteOrderFiles = async (fileIds) => {
  try {
    return await apiClient.delete(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.BULK_DELETE_ORDER_FILES}`, {
      data: { fileIds },
    });
  } catch (error) {
    console.error('Error in bulkDeleteOrderFiles:', error);
    throw error;
  }
};

/**
 * Delete an old purchase order (uses different API endpoint)
 * @param {string} orderId - Old Purchase order ID
 * @returns {Promise} API response
 */
export const deleteOldPurchaseOrder = async (orderId) => {
  try {
    // Based on the controller, old POs use the original API endpoint from order-create-po.controller.js
    return await apiClient.delete(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.FETCH_OLD_PO_PREVIEW}/${orderId}`);
  } catch (error) {
    console.error('Error in deleteOldPurchaseOrder:', error);
    throw error;
  }
};

/**
 * Bulk delete old purchase orders
 * @param {Array<string>} orderIds - Array of old purchase order IDs
 * @returns {Promise} API response
 */
export const bulkDeleteOldPurchaseOrders = async (orderIds) => {
  try {
    const ids = Array.isArray(orderIds) ? orderIds.join(',') : orderIds;
    return await apiClient.delete(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.BULK_DELETE_OLD_PO}?orderIds=${ids}`);
  } catch (error) {
    console.error('Error in bulkDeleteOldPurchaseOrders:', error);
    throw error;
  }
};

/**
 * Download purchase order as PDF
 * @param {string} poId - Purchase order ID
 * @param {Array} distributionCenters - Optional array of distribution centers to filter
 * @returns {Promise} PDF blob response
 */
export const downloadPOPDF = async (poId, distributionCenters = null, timeZone = null) => {
  try {
    const requestData = { poId };
    const tz = timeZone ?? (typeof Intl !== 'undefined' && Intl.DateTimeFormat ? Intl.DateTimeFormat().resolvedOptions().timeZone : null);
    if (tz) {
      requestData.timeZone = tz;
    }
    if (distributionCenters && Array.isArray(distributionCenters) && distributionCenters.length > 0) {
      requestData.distributionCenters = distributionCenters;
    }

    return await apiClient.post(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.DOWNLOAD_PDF}`, requestData, { responseType: 'blob' });
  } catch (error) {
    console.error('Error in downloadPOPDF:', error);
    console.error('Error details:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      headers: error.response?.headers,
    });
    throw error;
  }
};

/**
 * Download Purchase Order Sample Excel
 * @returns {Promise<Blob>} Excel blob response
 */
/**
 * Download Purchase Order Sample Excel with optional filters
 * @param {Object} [params={}] - Optional filter parameters
 * @param {string} [params.searchText] - Search text for filtering
 * @param {Object} [params.filters] - Filter parameters
 * @param {string[]} [params.filters.supplierIds] - Array of supplier IDs
 * @param {string[]} [params.filters.statuses] - Array of status values
 * @param {string[]} [params.filters.colourwayIds] - Array of colourway IDs
 * @param {string} [params.timezone] - Timezone for date conversion
 * @returns {Promise<Blob>} Excel blob response
 */
export const downloadPOSampleExcel = async (params = {}) => {
  const userInfo = localStorageService.get('loggedInUserInfo');

  // Build query parameters
  const queryParams = new URLSearchParams();
  if (params?.searchText?.trim()) {
    queryParams.append('searchText', params.searchText.trim());
  }
  if (params?.filters) {
    if (Array.isArray(params.filters.supplierIds) && params.filters.supplierIds.length > 0) {
      queryParams.append('supplierIds', params.filters.supplierIds.join(','));
    }
    if (Array.isArray(params.filters.statuses) && params.filters.statuses.length > 0) {
      queryParams.append('statuses', params.filters.statuses.join(','));
    }
    if (Array.isArray(params.filters.colourwayIds) && params.filters.colourwayIds.length > 0) {
      queryParams.append('colourwayIds', params.filters.colourwayIds.join(','));
    }
  }
  if (params?.timezone) {
    queryParams.append('timezone', params.timezone);
  }

  const queryString = queryParams.toString();
  const url = `${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.EXPORT_SAMPLE}${queryString ? `?${queryString}` : ''}`;

  const config = {
    headers: userInfo ? { Authorization: `Bearer ${userInfo.token}` } : {},
    responseType: 'arraybuffer',
    withCredentials: true,
  };

  const response = await axios.post(url, {}, config);
  return new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

/**
 * Update PO status (approve/reject/cancel/deliver/revise) by supplier or admin
 * @param {string} orderId - Purchase order ID
 * @param {Object} statusData - Status update data {status, comment, deliveryDate}
 * @returns {Promise} API response
 */
export const updatePOStatus = async (orderId, statusData) => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const payload = {
      ...statusData,
      timezone,
    };
    return await apiClient.put(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.GENERAL_PRODUCT_PO}/${orderId}/status`, payload);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Cancel PO by admin/user
 * @param {string} orderId - Purchase order ID
 * @param {Object} cancelData - Cancel data {comment}
 * @returns {Promise} API response
 */
export const cancelPO = async (orderId, cancelData) => {
  try {
    return await updatePOStatus(orderId, { status: 'cancelled', comment: cancelData.comment });
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Deliver PO by admin/user
 * @param {string} orderId - Purchase order ID
 * @param {Object} deliverData - Deliver data {deliveryDate}
 * @returns {Promise} API response
 */
export const deliverPO = async (orderId, deliverData) => {
  try {
    return await updatePOStatus(orderId, { status: 'delivered', deliveryDate: deliverData.deliveryDate });
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Revise PO by admin/user
 * @param {string} orderId - Purchase order ID
 * @returns {Promise} API response
 */
export const revisePO = async (orderId) => {
  try {
    return await updatePOStatus(orderId, { status: 'revised' });
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Fetch PO logs and history for a specific purchase order
 * @param {string} purchaseOrderId - Purchase Order ID
 * @returns {Promise} API response with logs and history data
 */
export const fetchPOLogsAndHistory = async (purchaseOrderId) => {
  try {
    if (!purchaseOrderId) {
      throw new Error('Purchase Order ID is required');
    }
    return await apiClient.get(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.GET_LOGS_AND_HISTORY}/${purchaseOrderId}/logs-history`);
  } catch (error) {
    console.error('Error fetching PO logs and history:', error);
    throw error;
  }
};

/**
 * Fetch specific PO log entry by ID
 * @param {string} logId - Log entry ID
 * @returns {Promise} API response with log entry data
 */
export const fetchPOLogById = async (logId) => {
  try {
    if (!logId) {
      throw new Error('Log ID is required');
    }
    return await apiClient.get(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.GET_LOG_BY_ID}/${logId}`);
  } catch (error) {
    console.error('Error fetching PO log by ID:', error);
    throw error;
  }
};

/**
 * Fetch detailed PO log entry by ID with populated data for preview
 * @param {string} logId - Log entry ID
 * @returns {Promise} API response with detailed log entry data including populated fields
 */
export const fetchPOLogDetailsById = async (logId) => {
  try {
    if (!logId) {
      throw new Error('Log ID is required');
    }
    return await apiClient.get(`${apiEndpoint}${API.PRODUCT_PURCHASE_ORDER.GET_LOG_DETAILS_BY_ID}/${logId}`);
  } catch (error) {
    console.error('Error fetching detailed PO log by ID:', error);
    throw error;
  }
};

export default {
  fetchProductDetailsForPO,
  fetchCompanyDetailsForPO,
  createPurchaseOrder,
  updatePurchaseOrder,
  updateActualFields,
  deletePurchaseOrder,
  fetchPurchaseOrders,
  fetchNewPurchaseOrders,
  fetchOldPurchaseOrders,
  fetchPurchaseOrderDetails,
  bulkDeletePurchaseOrders,
  setDefaultPurchaseOrder,
  getPurchaseOrderTemplates,
  savePurchaseOrderTemplate,
  deletePurchaseOrderTemplate,
  exportPurchaseOrderData,
  duplicatePurchaseOrder,
  getPurchaseOrderHistory,
  fetchFilteredDevelopmentOrders,
  fetchSupplierFactories,
  createOrderFile,
  getOrderFilesList,
  getOrderFileDetails,
  updateOrderFile,
  deleteOrderFile,
  bulkDeleteOrderFiles,
  deleteOldPurchaseOrder,
  bulkDeleteOldPurchaseOrders,
  downloadPOPDF,
  downloadPOSampleExcel,
  updatePOStatus,
  cancelPO,
  deliverPO,
  revisePO,
  fetchPOLogsAndHistory,
  fetchPOLogById,
  fetchPOLogDetailsById,
};
